// What a compact Opportunities card says (ART-328) — one place for the band dashboard, the phone home and the
// roster, so an opportunity and a show invite read the same on every surface. Pure: the caller supplies who is
// looking and what they may see; nothing here fetches.
import type { components } from "@cntrl/api-client";
import { oppMoney, oppTitleParts, oppTypeLabel, ringOf } from "./oppDisplay.js";
import { formatDay, offerDaysLeft } from "../org/rosterFormat.js";

type OpportunityDto = components["schemas"]["OpportunityDto"];
type ShowInviteDto = components["schemas"]["ShowInviteDto"];
type ShowRequestView = components["schemas"]["ShowRequestView"];

/** Reply-by turns red at this many days or fewer — the Needs-attention rule. */
const URGENT_DAYS = 3;

export interface CompactRowModel {
  tag: string;
  date: string | null;
  title: string;
  place: string | null;
  meta: string | null;
  urgent: boolean;
  /** "creator" ⇒ Edit; "invitee" ⇒ Details. */
  side: "creator" | "invitee" | "viewer";
  /** A show request waiting on the team (blue tag). */
  tone?: "request";
  answered: "in" | "out" | null;
}

/** The viewer's own answer, as the card's meta says it. */
export function answerLine(answer: "in" | "out" | null, placement = false): string | null {
  if (!answer) return null;
  if (placement) return answer === "in" ? "You approved" : "You passed";
  return answer === "in" ? "You're in" : "You're out";
}

function counts(responses: readonly (string | null | undefined)[]): string | null {
  const rings = responses.map(ringOf);
  const n = (r: string) => rings.filter((x) => x === r).length;
  const parts = [
    n("accepted") ? `${n("accepted")} in` : null,
    n("pending") ? `${n("pending")} waiting` : null,
    n("declined") ? `${n("declined")} out` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** An opportunity. `moneyCents` is the money-gated figure, passed only for a viewer the gate admits. */
export function opportunityRow(
  opp: OpportunityDto,
  myPersonId: string | null | undefined,
  moneyCents?: number | null,
): CompactRowModel {
  const { title, loc } = oppTitleParts(opp);
  const isCreator = !!myPersonId && opp.createdByPersonId === myPersonId;
  const mine = (opp.invites ?? []).find((i) => i.inviteePersonId === myPersonId);
  const ring = mine ? ringOf(mine.response) : null;
  const answered = ring === "accepted" ? "in" : ring === "declined" ? "out" : null;
  const placement = opp.type === "pr" && opp.prSubtype === "placement";
  const pay = oppMoney(moneyCents ?? undefined) ?? null;
  const meta = isCreator
    ? counts((opp.invites ?? []).map((i) => i.response)) ?? "No one invited yet"
    : !mine
      ? pay ?? counts((opp.invites ?? []).map((i) => i.response))
      : answered
        ? answerLine(answered, placement)
        : pay ? `${pay} · waiting on you` : "Waiting on you";
  return {
    tag: oppTypeLabel(opp),
    date: opp.date ? formatDay(opp.date) : null,
    title,
    place: loc ?? null,
    meta,
    // ART-338: the creator's row turns red once anybody passes — it stays on the card for exactly that reason.
    urgent: isCreator && (opp.invites ?? []).some((i) => ringOf(i.response) === "declined"),
    side: isCreator ? "creator" : mine ? "invitee" : "viewer",
    answered,
  };
}

/** A show or hold the viewer is on the member list of. */
export function showInviteRow(invite: ShowInviteDto): CompactRowModel {
  const ring = ringOf(invite.myRsvp);
  const answered = ring === "accepted" ? "in" : ring === "declined" ? "out" : null;
  const hold = invite.status === "hold";
  const place = [invite.city?.trim(), invite.state?.trim()].filter(Boolean).join(", ") || null;
  let meta: string | null = answerLine(answered);
  let urgent = false;
  if (!answered) {
    const days = hold ? offerDaysLeft(invite.holdExpiresOn) : null;
    if (days != null && days >= 0) {
      meta = `Reply by ${formatDay(invite.holdExpiresOn)}`;
      urgent = days <= URGENT_DAYS;
    } else {
      meta = "Waiting on you";
    }
  }
  return {
    tag: hold ? "Show · hold" : "Show",
    date: invite.date ? formatDay(invite.date) : null,
    title: invite.venueName?.trim() || place || "Show",
    place: invite.venueName?.trim() ? place : null,
    meta,
    urgent,
    side: "invitee",
    answered,
  };
}

/** A show request still waiting on the team (ART-327) — only ever listed for the people who decide it. The money is
 *  there only when the read carried it (a decider with show money). */
export function showRequestRow(r: ShowRequestView): CompactRowModel {
  const days = offerDaysLeft(r.goodThrough);
  const left = days == null ? null : days < 0 ? "reply overdue" : days === 0 ? "reply today" : `${days}d left`;
  const meta = [oppMoney(r.terms?.amountCents ?? undefined), r.sentBy?.org ?? r.sentBy?.name ?? null, left]
    .filter(Boolean).join(" · ") || null;
  return {
    tag: "Show request",
    date: r.date ? formatDay(r.date) : null,
    title: r.venueName?.trim() || r.buyerVenue?.trim() || "TBD",
    place: null,
    meta,
    urgent: days != null && days <= URGENT_DAYS,
    side: "invitee",
    answered: null,
    tone: "request",
  };
}

/** The requests an Opportunities card lists: the ones still waiting on an answer. */
export function openRequests(items: readonly ShowRequestView[] | null | undefined): ShowRequestView[] {
  return (items ?? []).filter((r) => r.status === "requested" && !r.decision);
}
